<template>
  <div class="pd-page">
    <!-- Loading -->
    <div v-if="loading" class="container py-4">
      <div class="skel skel-cover"></div>
      <div class="skel skel-line w-50 mt-3"></div>
      <div class="skel skel-line w-80 mt-2"></div>
    </div>

    <!-- Error / not found -->
    <div v-else-if="!product" class="pd-state">
      <i class="pi pi-exclamation-triangle"></i>
      <p>لم يتم العثور على هذا المنتج.</p>
      <router-link :to="backLink" class="pd-back-btn">رجوع</router-link>
    </div>

    <template v-else>
      <!-- Cover -->
      <div class="pd-cover" :style="coverStyle">
        <div class="pd-cover__overlay"></div>
        <div class="container pd-cover__content">
          <span class="pd-kind">{{ kind === 'course' ? 'كورس' : 'سنة دراسية' }}</span>
          <h1 class="pd-title">{{ title }}</h1>
          <p v-if="product.teacher" class="pd-teacher"><i class="pi pi-user"></i> {{ product.teacher }}</p>
        </div>
      </div>

      <div class="container py-4">
        <div class="row g-4">
          <!-- Left: description + features -->
          <div class="col-lg-7">
            <div class="pd-block">
              <h3 class="pd-h3">عن {{ kind === 'course' ? 'الكورس' : 'السنة الدراسية' }}</h3>
              <p class="pd-desc">{{ product.description || 'لا يوجد وصف متاح.' }}</p>
            </div>

            <div class="pd-block" v-if="stats.length">
              <h3 class="pd-h3">إحصائيات</h3>
              <div class="pd-stats">
                <div v-for="s in stats" :key="s.label" class="pd-stat">
                  <div class="pd-stat__value">{{ s.value }}</div>
                  <div class="pd-stat__label">{{ s.label }}</div>
                </div>
              </div>
            </div>

            <div class="pd-block">
              <h3 class="pd-h3">ماذا ستحصل عليه</h3>
              <ul class="pd-features">
                <li v-for="f in features" :key="f"><i class="pi pi-check-circle"></i> {{ f }}</li>
              </ul>
            </div>
          </div>

          <!-- Right: plans + subscribe -->
          <div class="col-lg-5">
            <div class="pd-plans-card">
              <h3 class="pd-h3">الخطط المتاحة</h3>
              <div v-if="plans.length" class="pd-plans">
                <button v-for="p in plans" :key="p.type" class="pd-plan"
                        :class="{ active: selectedPlan === p.type }" @click="selectedPlan = p.type">
                  <span class="pd-plan__name">{{ p.label }}</span>
                  <span class="pd-plan__price">{{ money(p.price) }}</span>
                </button>
              </div>
              <p v-else class="pd-noplans">لا توجد خطط متاحة حالياً.</p>

              <button class="pd-subscribe" :disabled="!product.accepts_subscriptions || !plans.length" @click="subscribe">
                <i class="pi pi-bolt"></i> اشترك الآن
              </button>
              <p v-if="!product.accepts_subscriptions" class="pd-hint">الاشتراك غير مفعّل لهذا المنتج حالياً.</p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { subscriptionService } from '@/services/subscriptionService.js';
import { productPlans, formatMoney } from '@/composables/subscriptionMeta.js';

export default {
  name: 'ProductDetails',
  props: {
    kind: { type: String, default: 'course' },
  },
  data() {
    return { product: null, loading: true, selectedPlan: null };
  },
  computed: {
    id() { return this.$route.params.id; },
    backLink() { return this.kind === 'course' ? '/courses' : '/academic-years'; },
    title() { return this.kind === 'course' ? this.product?.title : this.product?.name; },
    plans() { return productPlans(this.product); },
    coverStyle() {
      const img = this.product?.cover_image_link || this.product?.image_link;
      if (img) return { backgroundImage: `url(${img})` };
      return { background: `linear-gradient(135deg, ${this.product?.color || '#5B5FEF'}, #7c4dff)` };
    },
    stats() {
      // Only surface stats the backend actually provides.
      const s = [];
      if (this.plans.length) s.push({ label: 'خطط متاحة', value: this.plans.length });
      return s;
    },
    features() {
      return this.kind === 'course'
        ? ['وصول كامل لمحتوى الكورس', 'فيديوهات وملفات', 'امتحانات وواجبات', 'بنك أسئلة', 'متابعة مستمرة']
        : ['وصول كامل لمحتوى السنة الدراسية', 'الحصص والوحدات', 'امتحانات وواجبات', 'بنك أسئلة'];
    },
  },
  methods: {
    money: formatMoney,
    async load() {
      this.loading = true;
      try {
        if (this.kind === 'course') {
          this.product = await subscriptionService.course(this.id);
        } else {
          const years = await subscriptionService.academicYears();
          this.product = years.find(y => String(y.id) === String(this.id) || y.slug === this.id) || null;
        }
        if (this.plans.length) this.selectedPlan = this.plans[0].type;
      } catch {
        this.product = null;
      } finally {
        this.loading = false;
      }
    },
    subscribe() {
      this.$router.push({ path: '/subscribe', query: { type: this.kind, id: this.id, plan: this.selectedPlan || undefined } });
    },
  },
  mounted() { this.load(); },
};
</script>

<style scoped>
.pd-page { min-height: 60vh; padding-bottom: 5rem; }
.pd-cover {
  position: relative; min-height: 240px;
  background-size: cover; background-position: center;
  display: flex; align-items: flex-end;
}
.pd-cover__overlay {
  position: absolute; inset: 0;
  background: linear-gradient(to top, rgba(15,23,42,.82), rgba(15,23,42,.25));
}
.pd-cover__content { position: relative; padding: 1.5rem 0 1.75rem; color: #fff; }
.pd-kind {
  display: inline-block; background: rgba(255,255,255,.18); backdrop-filter: blur(4px);
  padding: .25rem .75rem; border-radius: 99px; font-size: .75rem; font-weight: 700; margin-bottom: .6rem;
}
.pd-title { font-size: clamp(1.5rem, 4vw, 2.2rem); font-weight: 800; margin: 0; }
.pd-teacher { margin: .5rem 0 0; font-weight: 700; opacity: .95; }
.pd-block { margin-bottom: 1.75rem; }
.pd-h3 { font-size: 1.05rem; font-weight: 800; color: #111827; margin-bottom: .75rem; }
.pd-desc { color: #4b5563; line-height: 1.9; font-size: .95rem; }
.pd-stats { display: flex; gap: 1rem; flex-wrap: wrap; }
.pd-stat { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 14px; padding: 1rem 1.4rem; text-align: center; }
.pd-stat__value { font-size: 1.5rem; font-weight: 800; color: #5B5FEF; }
.pd-stat__label { font-size: .8rem; color: #6B7280; }
.pd-features { list-style: none; padding: 0; margin: 0; display: grid; gap: .6rem; }
.pd-features li { display: flex; align-items: center; gap: .5rem; color: #374151; font-size: .92rem; }
.pd-features .pi-check-circle { color: #22c55e; }
.pd-plans-card {
  background: #fff; border: 1px solid rgba(91,95,239,.12); border-radius: 18px;
  padding: 1.4rem; position: sticky; top: 1rem;
  box-shadow: 0 8px 24px rgba(91,95,239,.08);
}
.pd-plans { display: grid; gap: .6rem; margin-bottom: 1rem; }
.pd-plan {
  display: flex; justify-content: space-between; align-items: center;
  border: 1.5px solid #e2e8f0; background: #fff; border-radius: 12px;
  padding: .8rem 1rem; cursor: pointer; transition: all .15s ease;
}
.pd-plan.active { border-color: #5B5FEF; background: rgba(91,95,239,.05); }
.pd-plan__name { font-weight: 700; color: #374151; }
.pd-plan__price { font-weight: 800; color: #5B5FEF; }
.pd-noplans { color: #94a3b8; font-size: .88rem; }
.pd-subscribe {
  width: 100%; background: linear-gradient(90deg, #5B5FEF, #7c4dff); color: #fff;
  border: none; border-radius: 12px; padding: .8rem; font-weight: 800; font-size: 1rem; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: .5rem;
}
.pd-subscribe:disabled { background: #cbd5e1; cursor: not-allowed; }
.pd-hint { font-size: .78rem; color: #94a3b8; text-align: center; margin: .6rem 0 0; }
.pd-state { text-align: center; padding: 4rem 1rem; color: #94a3b8; }
.pd-state .pi { font-size: 2.6rem; display: block; margin-bottom: .8rem; }
.pd-back-btn { display: inline-block; margin-top: .5rem; background: #5B5FEF; color: #fff; border-radius: 10px; padding: .5rem 1.2rem; font-weight: 700; text-decoration: none; }
.skel { background: #e9edf5; border-radius: 10px; position: relative; overflow: hidden; }
.skel::after { content: ''; position: absolute; inset: 0; transform: translateX(-100%); background: linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent); animation: sh 1.3s infinite; }
@keyframes sh { 100% { transform: translateX(100%); } }
.skel-cover { height: 200px; } .skel-line { height: 14px; } .w-50 { width: 50%; } .w-80 { width: 80%; }
</style>
